import {useLocation} from 'wouter';

import {useDocRevisionsContext} from '../providers/version';

import {useGetDocumentRevisionByDateMutation} from '../application/document-workflow/explore-view/hooks/requests/use-get-document-revision-by-date-mutation';
import {useDocumentSidebarState} from '../application/document-workflow/common/right-sidebar/hooks/use-document-sidebar-state';
import {EditRightPanelTab} from '../components/layout/page-layout/layout-typings';
import {useUrlNodeId} from '../application/document-workflow/common/use-url-node-id';

function getDocumentUrl(accession: string, revisionId?: string, nodeId?: string | null) {
    const url = [`/${accession}`];

    if (revisionId) {
        url.push(`/revisions/${revisionId}`);
    }

    if (nodeId) {
        url.push(`/@${encodeURIComponent(nodeId)}`);
    }

    return url.join('');
}

export function useGoToDeclaration(
    documentId: string,
    revisionId: string | undefined,
    rightSidebarProps: ReturnType<typeof useDocumentSidebarState>,
    shouldOpenHierarchyTree?: boolean
) {
    const versionContext = useDocRevisionsContext();
    const getDocumentRevisionByDate = useGetDocumentRevisionByDateMutation();

    const [, navigate] = useLocation();
    const focusedOntologyNode = useUrlNodeId();

    const goToDeclaration = async (accession: string | null, nodeId: string | null) => {
        if (!accession) {
            return;
        }

        const revisionInfo = versionContext.revisions?.find(
            ({revision}) => revision === revisionId
        );

        const revisionData =
            revisionId && revisionInfo
                ? await getDocumentRevisionByDate.mutateAsync({
                      accession,
                      createdBefore: revisionInfo?.createdAt
                  })
                : null;

        const url = getDocumentUrl(accession, revisionData?.revision, nodeId);

        navigate(url);

        if (!shouldOpenHierarchyTree) {
            return;
        }

        if (!rightSidebarProps.isSidebarOpen) {
            rightSidebarProps.openSidebar();
        }

        if (rightSidebarProps.selectedTab !== EditRightPanelTab.HIERARCHICAL_TREE) {
            rightSidebarProps.setSelectedTab(EditRightPanelTab.HIERARCHICAL_TREE);
        }
    };

    async function focusOntologyElement(nodeId: string) {
        const url = getDocumentUrl(documentId, revisionId, nodeId);

        const isNodeAlreadySelected = focusedOntologyNode === nodeId;

        if (!isNodeAlreadySelected) {
            navigate(url);

            return;
        }

        // if node was already selected, we still need to scroll to it,
        // that's why we clear the selection and then set it again;
        // also we use replace here to avoid adding new history entry
        const urlWithoutFocusedNode = getDocumentUrl(documentId, revisionId);
        navigate(urlWithoutFocusedNode, {replace: true});
        setTimeout(() => navigate(url, {replace: true}), 0);
    }

    return {goToDeclaration, focusOntologyElement, focusedOntologyNode};
}
